import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ReactNode, useState } from "react";

interface ChatListSectionProps {
  title: string;
  badgeCount: number;
  defaultOpen?: boolean;
  loading?: boolean;
  onToggle?: (isOpen: boolean) => void;
  children: ReactNode;
}

export function ChatListSection({
  title,
  badgeCount,
  defaultOpen = false,
  loading = false,
  onToggle,
  children,
}: ChatListSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const hasBadge = badgeCount > 0;

  const toggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      onToggle?.(next);
      return next;
    });
  };

  return (
    <div className="border-b border-zinc-200">
      <button
        type="button"
        onClick={toggle}
        className={`flex w-full items-center justify-between px-4 py-3 text-left hover:bg-zinc-50 ${
          hasBadge ? "bg-blue-50" : ""
        }`}
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDownIcon className="size-4 text-zinc-500" />
          ) : (
            <ChevronRightIcon className="size-4 text-zinc-500" />
          )}
          <span
            className={`text-sm font-medium ${
              hasBadge ? "font-semibold text-blue-700" : "text-zinc-700"
            }`}
          >
            {title}
          </span>
        </div>
        {hasBadge && (
          <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-blue-500 px-1.5 text-xs font-medium text-white">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="divide-y divide-zinc-100 bg-white">
          {loading ? (
            <div className="space-y-2 px-4 py-4">
              <div className="h-12 animate-pulse rounded bg-zinc-100" />
              <div className="h-12 animate-pulse rounded bg-zinc-100" />
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}
